"use client";

import { FetchButton } from "@/fetch-ui/FetchButton";
import { NavArrowDown } from "iconoir-react";
import { useReducedMotion } from "motion/react";
import { type MouseEvent, useMemo, useState } from "react";
import { useActiveRelease } from "../_hooks/useActiveRelease";
import { formatReleaseMonth } from "../_logic/release-groups";

interface ReleaseRailProps {
  /** Release months grouped by year, newest first: `[{ year: "2026", months: ["2026-10", ...] }]`. */
  years: { year: string; months: string[] }[];
}

/**
 * Sticky year and month index for the release log. The year being read stays open, other years
 * open on click, and the month being read is marked.
 *
 * @param years - Release months grouped by year, newest first.
 * @returns The month navigation.
 */
export function ReleaseRail({ years }: ReleaseRailProps): JSX.Element {
  // Hooks
  const allMonths = useMemo(() => years.flatMap((y) => y.months), [years]);
  const activeMonth = useActiveRelease(allMonths);
  const reduceMotion = useReducedMotion();

  // States
  const [openYears, setOpenYears] = useState<Set<string>>(new Set());

  // Derived values
  const activeYear = activeMonth.slice(0, 4);

  // Handlers
  const toggleYear = (year: string) => {
    setOpenYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) next.delete(year);
      else next.add(year);
      return next;
    });
  };

  const goToMonth = (event: MouseEvent<HTMLAnchorElement>, month: string) => {
    const section = document.getElementById(month);
    if (!section) return;
    event.preventDefault();
    section.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
    window.history.replaceState(null, "", `#${month}`);
  };

  // Render
  return (
    <nav
      aria-label="Release months"
      className="sticky top-28 -mx-1 flex max-h-[calc(100vh-8rem)] flex-col gap-6 overflow-y-auto px-1"
    >
      {years.map(({ year, months }) => {
        const isOpen = year === activeYear || openYears.has(year);
        return (
          <div key={year} className="flex flex-col gap-3">
            <FetchButton
              variant="text"
              color="neutral"
              onClick={() => toggleYear(year)}
              ariaExpanded={isOpen}
              rightIcon={
                <NavArrowDown
                  strokeWidth={1.75}
                  className={`size-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                />
              }
              className="w-fit"
            >
              {year}
            </FetchButton>
            {isOpen && (
              <ol className="flex flex-col gap-1">
                {months.map((month) => {
                  const isActive = month === activeMonth;
                  return (
                    <li key={month}>
                      <a
                        href={`#${month}`}
                        onClick={(event) => goToMonth(event, month)}
                        aria-current={isActive ? "location" : undefined}
                        className={`flex items-center gap-3 rounded-lg py-1 text-body-sm transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                          isActive
                            ? "text-landing-primary"
                            : "text-landing-secondary hover:text-[color:var(--landing-text-primary)]"
                        }`}
                      >
                        <span className="flex w-1.5 justify-center">
                          <span
                            className={`w-1.5 rounded-full transition-all duration-200 ${
                              isActive ? "h-3.5 bg-landing-dark" : "h-1.5 bg-[color:var(--landing-text-secondary)]/25"
                            }`}
                          />
                        </span>
                        {formatReleaseMonth(month, "month")}
                      </a>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        );
      })}
    </nav>
  );
}
